/**
 * LeafTestInputs — inline input fields for a SELECTED leaf in manual
 * tree-test mode (TASK-260). Moved out of ServiceManagementPage.tsx
 * (KAN-157 god-component split).
 *
 * Shape depends on the calculation method:
 *   calculator → one number input per client-facing required_input;
 *   classifier → a variant <select> built from pricing_data + a quantity input;
 *   pricelist  → one quantity input per pricing position.
 * Edits are pushed up via `onPatch` so the parent keeps the canonical
 * `TreeTestSelection` for this service.
 */
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import type { Service, TreeTestSelection } from '@/services/api/services';
import { clientInputFields, pricingItems } from '@/utils/serviceFormula';

export function LeafTestInputs({
  service,
  selection,
  onPatch,
}: {
  service: Service;
  selection: TreeTestSelection | undefined;
  onPatch: (serviceId: number, patch: Partial<TreeTestSelection>) => void;
}): JSX.Element | null {
  const method = service.calculation_method;

  if (method === 'calculator') {
    const fields = (service.chains || []).flatMap((c) => clientInputFields(c));
    // De-duplicate by name (chains may repeat shared inputs).
    const seen = new Set<string>();
    const unique = fields.filter((f) => {
      if (seen.has(f.name)) return false;
      seen.add(f.name);
      return true;
    });
    if (unique.length === 0) return null;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {unique.map((field) => (
          <label key={field.name} className="flex flex-col gap-0.5 text-xs">
            <span className="text-gray-500">
              {field.name}
              {field.unit ? ` (${field.unit})` : ''}
            </span>
            <Input
              type="number"
              inputMode="decimal"
              className="h-8"
              value={selection?.inputs?.[field.name] ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                const next = { ...(selection?.inputs ?? {}) };
                if (value === '') {
                  delete next[field.name];
                } else {
                  next[field.name] = Number(value);
                }
                onPatch(service.id, { inputs: next });
              }}
            />
          </label>
        ))}
      </div>
    );
  }

  if (method === 'classifier') {
    const items = pricingItems(service);
    const options = items.map((item) => {
      const value = (item.variant ?? item.work ?? item.name ?? '').trim();
      const label = [item.work ?? item.name, item.variant]
        .filter(Boolean)
        .join(' — ');
      return { value, label: label || value || '—' };
    });
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5 text-xs">
          <span className="text-gray-500">Вариант</span>
          <NativeSelect
            className="h-8"
            placeholder="Выберите вариант"
            options={options}
            value={selection?.variant ?? ''}
            onChange={(e) => onPatch(service.id, { variant: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-xs">
          <span className="text-gray-500">Количество</span>
          <Input
            type="number"
            inputMode="decimal"
            className="h-8"
            value={selection?.quantity ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              onPatch(service.id, {
                quantity: value === '' ? undefined : Number(value),
              });
            }}
          />
        </label>
      </div>
    );
  }

  if (method === 'pricelist') {
    const items = pricingItems(service);
    if (items.length === 0) return null;
    const current = selection?.items ?? [];
    const qtyFor = (work: string, variant?: string) =>
      current.find((it) => it.work === work && it.variant === variant)
        ?.quantity ?? '';
    return (
      <div className="space-y-1">
        {items.map((item, idx) => {
          const work = (item.work ?? item.name ?? '—').trim() || '—';
          const variant = item.variant;
          return (
            <label
              key={idx}
              className="flex items-center justify-between gap-2 text-xs"
            >
              <span className="text-gray-600 truncate">
                {work}
                {variant ? ` (${variant})` : ''}
              </span>
              <Input
                type="number"
                inputMode="decimal"
                className="h-8 w-24"
                value={qtyFor(work, variant)}
                onChange={(e) => {
                  const value = e.target.value;
                  const rest = current.filter(
                    (it) => !(it.work === work && it.variant === variant)
                  );
                  const nextItems =
                    value === ''
                      ? rest
                      : [
                          ...rest,
                          { work, variant, quantity: Number(value) },
                        ];
                  onPatch(service.id, { items: nextItems });
                }}
              />
            </label>
          );
        })}
      </div>
    );
  }

  return null;
}
